import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import type { Payload } from '@atlaskit/rovo-triggers';
import { ROVO_POST_MESSAGE_EVENT_TYPE } from '@atlaskit/rovo-triggers/post-message-to-pubsub';

import { IdentityUserAri } from '@atlassian/ari/identity/user';

import { cfetch } from '@confluence/network';

import {
	AGENT_MENTION_COMMENT_SAVED_EVENT,
	AGENT_RESOLVE_TIMEOUT_MS,
} from './agentMentionConstants.ts';

/** ADF document type accepted from comment editors — covers both DocNode and JSONDocNode call sites */
export type AdfDocument = DocNode | JSONDocNode;

export type AgentMentionInfo = {
	agentId: string;
	agentName: string;
	/** The full text of the paragraph containing the mention, with the mention name in position. */
	promptText: string;
};

type ResolvedAgent = {
	id: string;
	namedId: string;
	name: string;
};

/**
 * Cheap presence check — returns true on first matching mention without walking
 * the rest of the tree or building any intermediate objects. Use this on the
 * render path (e.g. on every keystroke for the FYI nudge) where the boolean is
 * the only thing that matters.
 *
 * Use `extractAgentMentionFromAdf` instead when you also need the agent details
 * (id, name, prompt text) — typically only on save.
 */
export function hasAgentMention(adf: AdfDocument | undefined): boolean {
	if (!adf?.content) {
		return false;
	}

	const stack: ADFEntity[] = [adf as ADFEntity];

	while (stack.length > 0) {
		const node = stack.pop();

		if (!node) {
			break;
		}

		if (
			node.type === 'mention' &&
			node.attrs?.userType === 'APP' &&
			typeof node.attrs?.id === 'string' &&
			node.attrs.id !== ''
		) {
			return true;
		}

		if (Array.isArray(node.content)) {
			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- ADF trees are shallow (typically <10 levels); linear in total node count
			for (const child of node.content) {
				if (child) {
					stack.push(child);
				}
			}
		}
	}

	return false;
}

/**
 * Walks an ADF tree and returns the first agent mention found.
 *
 * Uses `@atlaskit/adf-utils` traverse for depth-first traversal. Agent mentions
 * are identified by `userType === 'APP'` — this is currently the only reliable
 * indicator, as `appType === 'agent'` is not always present in ADF mention attrs.
 */
export function extractAgentMentionFromAdf(adf: AdfDocument | undefined): AgentMentionInfo | null {
	if (!adf?.content) {
		return null;
	}

	let result: AgentMentionInfo | null = null;

	// traverse walks the full tree even after we find a match — the early-return
	// guard below skips processing but doesn't stop traversal. This is acceptable
	// for comment ADF which is typically small (a few paragraphs).
	traverse(adf as ADFEntity, {
		mention: (node: ADFEntity, parent, index) => {
			if (result) {
				return; // already found one
			}

			if (node.attrs?.userType !== 'APP' || !node.attrs?.id) {
				return;
			}

			const agentId = node.attrs.id as string;
			const rawMentionText = (node.attrs.text as string) ?? '';
			const agentName = rawMentionText.replace(/^@/, '');

			// Build prompt text from the mention's sibling nodes in the parent,
			// preserving the original @-prefixed mention text for the prompt.
			const siblings = parent.node?.content ?? [];
			const promptText =
				siblings
					.map((sibling, j) => {
						if (j === index) {
							return rawMentionText;
						}
						return sibling?.type === 'text' && sibling.text ? sibling.text : '';
					})
					.join('')
					.trim() || rawMentionText;

			result = { agentId, agentName, promptText };
		},
	});

	return result;
}

/**
 * Resolves an Atlassian account ID (from the mention node) to an Agent Studio
 * UUID (needed by the CwR sidebar for agent pre-selection).
 *
 * Uses the Rovo assistance API with the same pattern as AssistanceServiceImpl.
 * Returns null on any error — agent pre-selection degrades gracefully (sidebar
 * defaults to Rovo).
 */
async function resolveAgentStudioId(
	accountId: string,
	cloudId: string,
	signal?: AbortSignal,
): Promise<ResolvedAgent | null> {
	try {
		const response = await cfetch(
			`/gateway/api/assist/rovo/v1/agents/accountid/${normalizeIdentityAccountId(accountId)}`,
			{
				method: 'GET',
				headers: {
					'x-product': 'confluence',
					'x-experience-id': 'ai-mate',
					'x-cloudid': cloudId,
				},
				signal,
			},
		);
		const agent = await response.json();

		return {
			id: agent.id,
			namedId: agent.named_id,
			name: agent.name,
		};
	} catch (err: unknown) {
		return null;
	}
}

function normalizeIdentityAccountId(accountId: string): string {
	try {
		return IdentityUserAri.parse(accountId).userId;
	} catch {
		return accountId;
	}
}

/**
 * Main entry point — called from comment save handlers.
 *
 * 1. Extracts the first agent mention from the comment ADF
 * 2. Dispatches a window event to open the CwR sidebar
 * 3. Resolves the agent's Studio ID (with timeout)
 * 4. Sends a `chat-new` postMessage via the rovo-triggers bridge, including
 *    conversation-channel tags so the backend can create a SessionAssociationPublic
 *    record linking the Rovo conversation to the Confluence page and comment.
 *
 * The PubSub system's `triggerLatest` queue ensures the `chat-new` event is
 * delivered even if the PubSubListener hasn't mounted yet when we publish.
 *
 * @param adf - The ADF document of the saved comment
 * @param cloudId - The Confluence cloud ID (from session data)
 * @param pageId - The Confluence page ID — used to build the `page:<pageId>` tag
 * @param commentId - The newly-created comment ID — used to build the `comment:<commentId>` tag
 */
async function setAgentSessionsProperty(pageId: string, userId: string): Promise<void> {
	const propertyKey = `cc-agent-mention-sessions:${userId}`;
	try {
		await cfetch(`/wiki/api/v2/pages/${pageId}/properties`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ key: propertyKey, value: true }),
		});
	} catch {
		// Best-effort write — 409 means already set; any other error is non-fatal.
	}
}

export function handleAgentMentionInComment(
	adf: AdfDocument,
	cloudId: string,
	pageId: string,
	commentId: string,
	userId?: string,
): void {
	const agentMention = extractAgentMentionFromAdf(adf);
	if (!agentMention) {
		return;
	}

	if (userId && pageId) {
		void setAgentSessionsProperty(pageId, userId);
	}

	// 1. Open the CwR sidebar via window event
	window.dispatchEvent(
		new CustomEvent(AGENT_MENTION_COMMENT_SAVED_EVENT, {
			detail: agentMention,
		}),
	);

	// 2. Resolve agent ID, then send chat-new.
	const prompt = agentMention.promptText;
	const sourceId = `agent-mention-${agentMention.agentId}-${Date.now()}`;

	// Build the comment-mention conversation tags so the backend can create a
	// SessionAssociationPublic record (linking this conversation to the page/comment)
	// and initialize the WORKING state. ARI-formatted IDs are used so the values
	// persisted to ERS are valid Teamwork Graph ARI references, as required by
	// conversational-ai-platform PRs 31101 and 31525. The BE extracts the ARI via
	// substringAfter("page:") / substringAfter("comment:") and stores it as
	// entityId / subEntityId on SessionAssociationPublic.
	const pageAri = pageId && cloudId ? `ari:cloud:confluence:${cloudId}:page/${pageId}` : '';
	const commentAri =
		commentId && cloudId ? `ari:cloud:confluence:${cloudId}:comment/${commentId}` : '';
	const tags =
		pageAri && commentAri
			? ['mention-in-comment', `page:${pageAri}`, `comment:${commentAri}`]
			: undefined;

	const sendChatNew = (resolvedAgentId: string | null) => {
		const payload: Payload = {
			type: 'chat-new',
			source: 'agentMentionComment',
			data: {
				...(resolvedAgentId ? { agentId: resolvedAgentId } : {}),
				sourceId,
				prompt,
				dialogues: [],
				mode: { useCurrentPageContext: true },
				...(tags ? { tags } : {}),
			},
		};
		window.postMessage(
			{
				eventType: ROVO_POST_MESSAGE_EVENT_TYPE,
				payload,
				payloadId: Math.floor(Math.random() * 1e6).toString(),
			},
			window.location.origin,
		);
	};

	// If cloudId is missing skip the resolve call and open chat without agent pre-selection;
	// the sidebar has already opened above since we confirmed an agent mention is present.
	if (!cloudId) {
		sendChatNew(null);
		return;
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), AGENT_RESOLVE_TIMEOUT_MS);

	void resolveAgentStudioId(agentMention.agentId, cloudId, controller.signal).then((resolved) => {
		clearTimeout(timeoutId);
		sendChatNew(resolved?.id ?? null);
	});
}
